import React, { useEffect, useState } from "react";
import { Badge, useTheme } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { send_request } from "../../utils/Request";
import useTaskInboxStore from "../pages/useTaskInboxStore";

const MAX_BADGE_COUNT = 99;

/** Solid fills tuned for legibility on the sidebar in light and dark mode. */
const BADGE_COLORS = {
  light: { bg: "#c5221f", text: "#ffffff", ring: "rgba(0, 0, 0, 0.08)" },
  dark: { bg: "#ef5350", text: "#ffffff", ring: "rgba(0, 0, 0, 0.45)" },
};

function inboxBadgeSx(theme) {
  const colors = BADGE_COLORS[theme.palette.mode] || BADGE_COLORS.light;

  return {
    display: "inline-flex",
    flexShrink: 0,
    "& .MuiBadge-badge": {
      minWidth: 16,
      height: 15,
      padding: "0 4px",
      fontSize: 10,
      fontWeight: 700,
      lineHeight: "15px",
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      borderRadius: "4px",
      top: -4,
      right: -7,
      transform: "none",
      border: "1.5px solid",
      borderColor: theme.palette.background.paper,
      boxSizing: "border-box",
      boxShadow: `0 0 0 1px ${colors.ring}`,
      backgroundColor: colors.bg,
      color: colors.text,
    },
  };
}

// Cache the combined inbox count so remounts show the last known badge immediately.
let cachedInboxCount = null;
let cachedAgentCount = null;
let agentCountPromise = null;

function formatBadgeCount(count) {
  if (!count || count <= 0) return null;
  return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : count;
}

function getAgentActiveCount() {
  const storeTotal = useTaskInboxStore.getState().taskInboxTotal;
  if (storeTotal > 0) {
    cachedAgentCount = storeTotal;
    return Promise.resolve(storeTotal);
  }

  if (cachedAgentCount !== null) {
    return Promise.resolve(cachedAgentCount);
  }

  if (agentCountPromise) return agentCountPromise;

  agentCountPromise = send_request(
    "project-service/agent/forms",
    "",
    { limit: 1, status: "ACTIVE" },
    "GET"
  )
    .then((res) => {
      const count = Number(res?.data?.total) || 0;
      cachedAgentCount = count;
      agentCountPromise = null;
      return count;
    })
    .catch(() => {
      agentCountPromise = null;
      return cachedAgentCount || 0;
    });

  return agentCountPromise;
}

async function fetchInboxCount() {
  const [legacyCount, agentCount] = await Promise.all([
    send_request("task/task/get_incomplete_task_count")
      .then((res) => Number(res?.data) || 0)
      .catch(() => 0),
    getAgentActiveCount(),
  ]);
  const total = legacyCount + agentCount;
  cachedInboxCount = total;
  return total;
}

/**
 * Inbox nav icon with an unread-style count badge (Gmail/Outlook pattern):
 * icon is always visible; a compact rounded-rect chip appears only when count > 0.
 */
function TaskNotificationsBadge() {
  const theme = useTheme();
  const [inboxCount, setInboxCount] = useState(cachedInboxCount);
  const storeTotal = useTaskInboxStore((s) => s.taskInboxTotal);
  const badgeContent = formatBadgeCount(inboxCount);

  useEffect(() => {
    if (storeTotal > 0 && cachedAgentCount !== storeTotal) {
      cachedAgentCount = storeTotal;
    }
  }, [storeTotal]);

  useEffect(() => {
    let cancelled = false;

    fetchInboxCount()
      .then((total) => {
        if (!cancelled) setInboxCount(total);
      })
      .catch(() => {
        if (!cancelled) setInboxCount((prev) => prev ?? 0);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (storeTotal <= 0) return;

    let cancelled = false;

    fetchInboxCount()
      .then((total) => {
        if (!cancelled) setInboxCount(total);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [storeTotal]);

  return (
    <Badge
      badgeContent={badgeContent}
      invisible={badgeContent == null}
      overlap="rectangular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      sx={inboxBadgeSx(theme)}
    >
      <InboxOutlinedIcon aria-hidden />
    </Badge>
  );
}

export default TaskNotificationsBadge;
